/**
 * A agenda como PAUTA por SESSÃO de julgamento (porte do branch do Rubens,
 * `85f7ad33`/`2c1221bc`).
 *
 * Um evento de sessão é UM processo numa sessão. A Agenda junta os que estão
 * na mesma sessão — mesmo tribunal, colegiado, modalidade e período — e,
 * dentro dela, separa por relator: é assim que o advogado lê uma pauta
 * ("3ª Turma, sessão virtual de 15 a 22/10: Des. Fulano, dois processos").
 *
 * Quem lê a sessão no texto da intimação é o backend
 * (`services/pauta_sessao.py`). Aqui só se agrupa e se escreve — e só o que o
 * texto disse: sem colegiado, a sessão se distingue pela sala e a tela diz
 * "Turma não informada"; sem encerramento, mostra a data designada e só.
 *
 * O que não é sessão (audiência, prazo, reunião) entra na mesma lista, na
 * ordem das datas, como linha própria: não há o que agrupar.
 */
import type { CalendarEvent } from '@/api/client';
import { toLocalISODate, toTitleCaseClasse } from '@/lib/formatters';

export interface ProcessoNaPauta {
  eventoId: string;
  /** O evento de origem — a linha do processo abre o detalhe dele. */
  evento: CalendarEvent;
  numero: string | null;
  classe: string | null;
  partes: string | null;
}

export interface RelatorNaPauta {
  nome: string;
  processos: ProcessoNaPauta[];
}

export interface SessaoNaPauta {
  tipo: 'sessao';
  chave: string;
  inicio: string;
  fim: string | null;
  horaConhecida: boolean;
  tribunal: string | null;
  colegiado: string | null;
  modalidade: string | null;
  local: string | null;
  relatores: RelatorNaPauta[];
  total: number;
}

export interface EventoAvulso {
  tipo: 'avulso';
  chave: string;
  inicio: string;
  evento: CalendarEvent;
}

export type ItemDaPauta = SessaoNaPauta | EventoAvulso;

const SEM_RELATOR = 'Relator não informado';

export function agruparPauta(
  eventos: CalendarEvent[],
  { recentesPrimeiro = false }: { recentesPrimeiro?: boolean } = {},
): ItemDaPauta[] {
  const sessoes = new Map<string, SessaoNaPauta>();
  const itens: ItemDaPauta[] = [];

  for (const e of eventos) {
    // Só agrupa a sessão que veio da intimação. A marcada à mão não tem
    // tribunal, Turma nem processo lidos — agrupada, perderia o título.
    if (e.tipo !== 'sessao' || e.origem !== 'djen') {
      itens.push({ tipo: 'avulso', chave: e.id, inicio: e.data_inicio, evento: e });
      continue;
    }
    // Sem colegiado, a SALA distingue duas sessões do mesmo dia — juntar
    // tudo em "Turma não informada" fundiria sessões diferentes.
    const chave = [
      e.sessao_tribunal ?? '',
      e.sessao_colegiado ?? `sala:${e.local ?? ''}`,
      e.sessao_modalidade ?? '',
      e.data_inicio,
      e.data_fim ?? '',
    ].join('|');

    let s = sessoes.get(chave);
    if (!s) {
      s = {
        tipo: 'sessao',
        chave,
        inicio: e.data_inicio,
        fim: e.data_fim ?? null,
        horaConhecida: e.hora_conhecida !== false,
        tribunal: e.sessao_tribunal ?? null,
        colegiado: e.sessao_colegiado ?? null,
        modalidade: e.sessao_modalidade ?? null,
        local: e.local ?? null,
        relatores: [],
        total: 0,
      };
      sessoes.set(chave, s);
      itens.push(s);
    }
    const nome = e.sessao_relator ?? SEM_RELATOR;
    let r = s.relatores.find((x) => x.nome === nome);
    if (!r) {
      r = { nome, processos: [] };
      s.relatores.push(r);
    }
    r.processos.push({
      eventoId: e.id,
      evento: e,
      numero: e.processo_numero ?? null,
      classe: e.processo_classe ? toTitleCaseClasse(e.processo_classe) : null,
      partes: e.parte_principal ?? null,
    });
    s.total += 1;
  }

  const ordem = recentesPrimeiro ? -1 : 1;
  return itens.sort((a, b) => ordem * a.inicio.localeCompare(b.inicio));
}

// ---------------------------------------------------------------------------
// Como se escreve
// ---------------------------------------------------------------------------

function aoMeioDia(diaIso: string): Date {
  return new Date(`${diaIso}T12:00:00`);
}

/** 9h, 11h30 — a hora como se fala. */
export function horaFalada(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

/** 15/10 */
export function diaMes(iso: string): string {
  const [, mes, dia] = toLocalISODate(iso).split('-');
  return `${dia}/${mes}`;
}

function diasEntre(hojeIso: string, diaIso: string): number {
  return Math.round((aoMeioDia(diaIso).getTime() - aoMeioDia(hojeIso).getTime()) / 86_400_000);
}

/** "hoje", "amanhã", "em 5 dias", "em curso", "encerra hoje" — e, olhando
 *  para trás (tela Calendário), "ontem", "há 3 dias", "encerrada". */
export function estadoDaSessao(s: Pick<SessaoNaPauta, 'inicio' | 'fim'>, hojeIso: string): string {
  const ini = toLocalISODate(s.inicio);
  const fim = s.fim ? toLocalISODate(s.fim) : null;
  if (fim && ini < hojeIso && hojeIso <= fim) return fim === hojeIso ? 'encerra hoje' : 'em curso';
  if (fim && fim < hojeIso) return 'encerrada';
  const n = diasEntre(hojeIso, ini);
  if (n === 0) return 'hoje';
  if (n === -1) return 'ontem';
  if (n < 0) return `há ${-n} dias`;
  if (n === 1) return 'amanhã';
  return `em ${n} dias`;
}

/** A frase do quando — só o que a intimação disse. */
export function quandoDaSessao(s: SessaoNaPauta, hojeIso: string): string {
  const ini = toLocalISODate(s.inicio);
  const rotuloDia = ini === hojeIso ? 'hoje' : diaMes(s.inicio);
  const hora = s.horaConhecida ? horaFalada(s.inicio) : null;
  if (s.fim) {
    // A hora entre parênteses, colada à data a que pertence, e "até" no lugar
    // de "a" (que se lê "à"). Antes: "de 13/10, 11h30, a 20/10, 11h29" —
    // as vírgulas misturavam data e hora (o dono, 08/10/2026).
    const horaFim = s.horaConhecida ? ` (${horaFalada(s.fim)})` : '';
    return `de ${diaMes(s.inicio)}${hora ? ` (${hora})` : ''} até ${diaMes(s.fim)}${horaFim}`;
  }
  if (s.modalidade === 'virtual') {
    // A pauta do PJe dá uma data só, a "designada". Não se diz se é início
    // ou fim: o texto não diz.
    return `designada para ${rotuloDia}${hora ? ` (${hora})` : ''}`;
  }
  return hora ? `${rotuloDia} às ${hora}` : rotuloDia;
}

export function tituloDaSessao(s: SessaoNaPauta): string {
  const orgao = s.colegiado ?? 'Turma não informada';
  return s.tribunal ? `${s.tribunal} · ${orgao}` : orgao;
}
